import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plug, Loader2, CheckCircle2, AlertTriangle, Trash2, RefreshCw, ExternalLink, KeyRound, Server } from 'lucide-react';
import { toast } from 'sonner';

const SG_EMPTY = { api_key: '', sender_email: '', sender_name: '' };
const SMTP_EMPTY = { host: '', port: 587, encryption: 'tls', username: '', password: '', sender_email: '', sender_name: '' };

export const EmailProvidersPage = () => {
  const { api } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sg, setSg] = useState(SG_EMPTY);
  const [smtp, setSmtp] = useState(SMTP_EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/email/providers');
      setProviders(r.data?.providers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const connectSendgrid = async () => {
    if (!sg.api_key.trim() || !sg.sender_email.trim()) return toast.error('API key and sender email are required');
    setSaving(true);
    try {
      const r = await api.post('/email/providers/sendgrid', sg);
      const verified = r.data?.provider?.sender_verified;
      toast.success(verified ? 'SendGrid connected & sender verified!' : 'SendGrid connected — verify your sender to start sending.');
      setSg(SG_EMPTY); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not connect SendGrid'); }
    finally { setSaving(false); }
  };

  const connectSmtp = async () => {
    if (!smtp.host.trim() || !smtp.sender_email.trim()) return toast.error('SMTP host and sender email are required');
    setSaving(true);
    try {
      await api.post('/email/providers/smtp', { ...smtp, port: Number(smtp.port) });
      toast.success('SMTP server connected & verified!');
      setSmtp(SMTP_EMPTY); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not connect SMTP server'); }
    finally { setSaving(false); }
  };

  const reverify = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/email/providers/${id}/verify`);
      toast[r.data?.provider?.sender_verified ? 'success' : 'message'](
        r.data?.provider?.sender_verified ? 'Verified ✓' : 'Connected, but sender still not verified.'
      );
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    finally { setBusyId(null); }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this provider? Scheduled sends will fail until you reconnect.')) return;
    setBusyId(id);
    try { await api.delete(`/email/providers/${id}`); toast.success('Disconnected'); await load(); }
    catch { toast.error('Could not disconnect'); }
    finally { setBusyId(null); }
  };

  const isSmtp = (p) => p.provider === 'smtp';

  return (
    <EmailSectionLayout>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-6">
          {providers.length > 0 && (
            <Card data-testid="connected-provider-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Plug className="w-4 h-4 text-indigo-600" /> Connected provider</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {providers.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border border-zinc-200">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-zinc-900 flex items-center gap-1.5">
                          {isSmtp(p) ? <><Server className="w-4 h-4 text-violet-600" /> SMTP</> : 'SendGrid'}
                        </span>
                        {p.sender_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" /> {isSmtp(p) ? 'Connected' : 'Sender verified'}</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><AlertTriangle className="w-3 h-3 mr-1" /> Sender unverified</Badge>
                        )}
                      </div>
                      <div className="text-sm text-zinc-600">From: <span className="font-medium">{p.sender_name}</span> &lt;{p.sender_email}&gt;</div>
                      {isSmtp(p) ? (
                        <div className="text-xs text-zinc-400 flex items-center gap-1"><Server className="w-3 h-3" /> {p.smtp_host}:{p.smtp_port} · {String(p.smtp_encryption).toUpperCase()} · {p.smtp_username || 'no-auth'}</div>
                      ) : (
                        <div className="text-xs text-zinc-400 flex items-center gap-1"><KeyRound className="w-3 h-3" /> {p.key_masked}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={busyId === p.id} onClick={() => reverify(p.id)} data-testid={`reverify-${p.id}`}>
                        {busyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        <span className="ml-1.5">Re-check</span>
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" disabled={busyId === p.id} onClick={() => disconnect(p.id)} data-testid={`disconnect-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-zinc-400">You can connect one provider at a time. Connecting a new one replaces the current one.</p>
              </CardContent>
            </Card>
          )}

          <Card data-testid="connect-provider-form">
            <CardHeader>
              <CardTitle className="text-base">{providers.length ? 'Switch / replace provider' : 'Connect an email provider'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="sendgrid">
                <TabsList className="mb-4">
                  <TabsTrigger value="sendgrid" data-testid="tab-sendgrid">SendGrid</TabsTrigger>
                  <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP (any provider)</TabsTrigger>
                </TabsList>

                <TabsContent value="sendgrid" className="space-y-4">
                  <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1">
                    <p>1. Create a <span className="font-medium">Full Access</span> (or Mail Send) API key in SendGrid → Settings → API Keys.</p>
                    <p>2. Verify your sender email in SendGrid → Sender Authentication.</p>
                    <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                      Open SendGrid API keys <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="api_key">SendGrid API key</Label>
                    <Input id="api_key" type="password" placeholder="SG.xxxxxxxx" value={sg.api_key}
                      onChange={(e) => setSg({ ...sg, api_key: e.target.value })} data-testid="provider-api-key" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="sg_sender_email">Verified sender email</Label>
                      <Input id="sg_sender_email" type="email" placeholder="you@yourdomain.com" value={sg.sender_email}
                        onChange={(e) => setSg({ ...sg, sender_email: e.target.value })} data-testid="provider-sender-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="sg_sender_name">Sender name</Label>
                      <Input id="sg_sender_name" placeholder="Your Brand" value={sg.sender_name}
                        onChange={(e) => setSg({ ...sg, sender_name: e.target.value })} data-testid="provider-sender-name" />
                    </div>
                  </div>
                  <Button onClick={connectSendgrid} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="provider-connect-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                    {providers.length ? 'Replace & verify' : 'Connect & verify'}
                  </Button>
                </TabsContent>

                <TabsContent value="smtp" className="space-y-4">
                  <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-1">
                    <p>Use any SMTP provider — Gmail/Google Workspace, Outlook/Microsoft 365, Mailgun, Amazon SES, Postmark, your own server, etc.</p>
                    <p className="text-xs text-zinc-500">Tip: most providers use port <b>587</b> with <b>STARTTLS</b>, or port <b>465</b> with <b>SSL</b>. For Gmail/Outlook use an <b>app password</b>, not your login password.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="smtp_host">SMTP host</Label>
                      <Input id="smtp_host" placeholder="smtp.gmail.com" value={smtp.host}
                        onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} data-testid="smtp-host" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_port">Port</Label>
                      <Input id="smtp_port" type="number" placeholder="587" value={smtp.port}
                        onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} data-testid="smtp-port" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Encryption</Label>
                    <Select value={smtp.encryption} onValueChange={(v) => setSmtp({ ...smtp, encryption: v, port: v === 'ssl' ? 465 : 587 })}>
                      <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tls">STARTTLS (port 587)</SelectItem>
                        <SelectItem value="ssl">SSL/TLS (port 465)</SelectItem>
                        <SelectItem value="none">None (not recommended)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_username">Username</Label>
                      <Input id="smtp_username" placeholder="you@yourdomain.com" value={smtp.username}
                        onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} data-testid="smtp-username" autoComplete="off" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_password">Password / app password</Label>
                      <Input id="smtp_password" type="password" placeholder="••••••••" value={smtp.password}
                        onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} data-testid="smtp-password" autoComplete="new-password" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_sender_email">Sender email (From)</Label>
                      <Input id="smtp_sender_email" type="email" placeholder="you@yourdomain.com" value={smtp.sender_email}
                        onChange={(e) => setSmtp({ ...smtp, sender_email: e.target.value })} data-testid="smtp-sender-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_sender_name">Sender name</Label>
                      <Input id="smtp_sender_name" placeholder="Your Brand" value={smtp.sender_name}
                        onChange={(e) => setSmtp({ ...smtp, sender_name: e.target.value })} data-testid="smtp-sender-name" />
                    </div>
                  </div>
                  <Button onClick={connectSmtp} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="smtp-connect-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                    {providers.length ? 'Replace & test connection' : 'Connect & test'}
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}
    </EmailSectionLayout>
  );
};

export default EmailProvidersPage;
